"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { SessionSidebar } from "./SessionSidebar";
import { ChatWindow } from "./ChatWindow";
import { ModelsConfig } from "./ModelsConfig";
import { SkillsConfig } from "./SkillsConfig";
import { ProfileFileEditor } from "./ProfileFileEditor";
import { BranchNavigator } from "./BranchNavigator";
import { useTheme } from "@/hooks/useTheme";
import { useIsMobile } from "@/hooks/useIsMobile";
import type { SessionInfo, SessionTreeNode } from "@/lib/types";
import type { ChatInputHandle } from "./ChatInput";
import type { SessionStatsInfo } from "@/lib/pi-types";

type SessionCopyField = "file" | "id";

function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return Promise.resolve();
  } catch {
    return Promise.reject();
  }
}

type AppShellUser = {
  id: string;
  username: string;
  role?: "admin" | "user";
  remainingChats?: number;
  membershipPlan?: "free" | "monthly" | "yearly" | "lifetime";
  membershipStatus?: "active" | "expired";
  membershipExpiresAt?: string | null;
  monthlyQuota?: number;
  quotaPeriodEnd?: string | null;
};

type MembershipPlanConfig = {
  id: "free" | "monthly" | "yearly" | "lifetime";
  name: string;
  priceLabel: string;
  price: number | null;
  originalPrice: number | null;
  discountLabel: string;
  discountEnabled: boolean;
  monthlyQuota: number;
  desc: string;
};

const PLAN_LABELS: Record<string, string> = {
  free: "免费",
  monthly: "月卡",
  yearly: "年卡",
  lifetime: "永久卡",
};

const DEFAULT_MEMBERSHIP_PLANS: MembershipPlanConfig[] = [
  { id: "free", name: "免费试用", priceLabel: "¥0", price: 0, originalPrice: null, discountLabel: "", discountEnabled: false, monthlyQuota: 10, desc: "适合首次体验恋爱参谋。" },
  { id: "monthly", name: "月卡", priceLabel: "¥99/月", price: 99, originalPrice: null, discountLabel: "", discountEnabled: false, monthlyQuota: 90, desc: "适合持续复盘聊天和约会推进。" },
  { id: "yearly", name: "年卡", priceLabel: "¥699/年", price: 699, originalPrice: null, discountLabel: "", discountEnabled: false, monthlyQuota: 150, desc: "适合长期经营关系和高频咨询。" },
  { id: "lifetime", name: "永久卡", priceLabel: "¥1899/一次性", price: 1899, originalPrice: null, discountLabel: "", discountEnabled: false, monthlyQuota: 300, desc: "适合长期使用，拥有最高月度额度。" },
];

function formatDate(value?: string | null): string {
  if (!value) return "不限";
  return new Date(value).toLocaleDateString("zh-CN");
}

function planPriceSuffix(planId: MembershipPlanConfig["id"]): string {
  if (planId === "monthly") return "/月";
  if (planId === "yearly") return "/年";
  if (planId === "lifetime") return "/一次性";
  return "";
}

function formatPlanPrice(value: number | null | undefined, planId: MembershipPlanConfig["id"]): string | null {
  if (value === null || value === undefined) return null;
  const amount = Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
  return `¥${amount}${planPriceSuffix(planId)}`;
}

function hasPlanDiscount(plan: MembershipPlanConfig): boolean {
  return Boolean(plan.discountLabel) || (plan.originalPrice !== null && plan.price !== null && plan.originalPrice > plan.price);
}

function getPlanBadges(plan: MembershipPlanConfig, discountActive: boolean): string[] {
  const badges: string[] = [];
  if (plan.id === "lifetime") badges.push("热销");
  if (discountActive && plan.discountLabel) badges.push(plan.discountLabel);
  return badges;
}

export function AppShell({ user, onUserRefresh, onLogout }: { user?: AppShellUser; onUserRefresh?: () => void; onLogout?: () => void }) {
  const router = useRouter();
  const { isDark, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [selectedSession, setSelectedSession] = useState<SessionInfo | null>(null);
  // When user clicks +, we only store the cwd — no fake session id
  const [newSessionCwd, setNewSessionCwd] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sessionKey, setSessionKey] = useState(0);
  const [explorerRefreshKey, setExplorerRefreshKey] = useState(0);
  const [modelsConfigOpen, setModelsConfigOpen] = useState(false);
  const [modelsRefreshKey, setModelsRefreshKey] = useState(0);
  const [skillsConfigOpen, setSkillsConfigOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipForceShowPlans, setMembershipForceShowPlans] = useState(false);
  const [membershipNotice, setMembershipNotice] = useState<string | null>(null);
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlanConfig[]>(DEFAULT_MEMBERSHIP_PLANS);
  const [paymentQrUrl, setPaymentQrUrl] = useState<string | null>(null);
  const [paymentWechatId, setPaymentWechatId] = useState("");
  const [announcementEnabled, setAnnouncementEnabled] = useState(false);
  const [announcementHtml, setAnnouncementHtml] = useState("");
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarReady, setMobileSidebarReady] = useState(false);
  const [profilePromptRequest, setProfilePromptRequest] = useState<{ id: string; prompt: string } | null>(null);
  const chatInputRef = useRef<ChatInputHandle | null>(null);
  const topBarRef = useRef<HTMLDivElement>(null);

  // Branch navigator state — populated by ChatWindow via onBranchDataChange
  const [branchTree, setBranchTree] = useState<SessionTreeNode[]>([]);
  const [branchActiveLeafId, setBranchActiveLeafId] = useState<string | null>(null);
  const branchLeafChangeFnRef = useRef<((leafId: string | null) => void) | null>(null);

  const handleBranchDataChange = useCallback((tree: SessionTreeNode[], activeLeafId: string | null, onLeafChange: (leafId: string | null) => void) => {
    setBranchTree(tree);
    setBranchActiveLeafId(activeLeafId);
    branchLeafChangeFnRef.current = onLeafChange;
  }, []);

  const handleBranchLeafChange = useCallback((leafId: string | null) => {
    branchLeafChangeFnRef.current?.(leafId);
  }, []);

  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);
  const systemBtnRef = useRef<HTMLButtonElement>(null);

  const handleSystemPromptChange = useCallback((prompt: string | null) => {
    setSystemPrompt(prompt);
  }, []);

  // Session stats (tokens + cost) — populated by ChatWindow, displayed in top bar
  const [sessionStats, setSessionStats] = useState<SessionStatsInfo | null>(null);
  const handleSessionStatsChange = useCallback((stats: SessionStatsInfo | null) => {
    setSessionStats(stats);
  }, []);
  const [copiedSessionField, setCopiedSessionField] = useState<SessionCopyField | null>(null);
  const sessionCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCopySessionField = useCallback((field: SessionCopyField, value: string) => {
    void copyText(value).then(() => {
      if (sessionCopyTimerRef.current) clearTimeout(sessionCopyTimerRef.current);
      setCopiedSessionField(field);
      sessionCopyTimerRef.current = setTimeout(() => setCopiedSessionField(null), 1400);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (sessionCopyTimerRef.current) clearTimeout(sessionCopyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setMobileSidebarReady(true);
  }, []);

  // Context usage — populated by ChatWindow, displayed in top bar
  const [contextUsage, setContextUsage] = useState<{ percent: number | null; contextWindow: number; tokens: number | null } | null>(null);
  const handleContextUsageChange = useCallback((usage: { percent: number | null; contextWindow: number; tokens: number | null } | null) => {
    setContextUsage(usage);
  }, []);

  // Single active panel — only one dropdown open at a time
  const [activeTopPanel, setActiveTopPanel] = useState<"branches" | "system" | "session" | null>(null);
  const [topPanelPos, setTopPanelPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const toggleTopPanel = useCallback((panel: "branches" | "system" | "session") => {
    if (isMobile) setSidebarOpen(false);
    setActiveTopPanel((cur) => cur === panel ? null : panel);
  }, [isMobile]);

  const openSessionStatsPanel = useCallback(() => {
    if (isMobile) setSidebarOpen(false);
    setActiveTopPanel("session");
  }, [isMobile]);

  const handleSidebarToggle = useCallback(() => {
    if (isMobile) setActiveTopPanel(null);
    setSidebarOpen((open) => !open);
  }, [isMobile]);

  useEffect(() => {
    if (!activeTopPanel || !topBarRef.current) return;
    const update = () => {
      const rect = topBarRef.current!.getBoundingClientRect();
      setTopPanelPos({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(topBarRef.current);
    return () => ro.disconnect();
  }, [activeTopPanel]);

  // Right panel — file tabs only
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [editingProfileFile, setEditingProfileFile] = useState<{ filePath: string; fileName: string } | null>(null);

  const handleAtMention = useCallback((relativePath: string) => {
    chatInputRef.current?.insertText("`" + relativePath + "`");
  }, []);

  const handleLogout = useCallback(async () => {
    await fetch("/api/app-auth/logout", { method: "POST" });
    onLogout?.();
  }, [onLogout]);

  useEffect(() => {
    fetch("/api/app-settings")
      .then((res) => res.ok ? res.json() : null)
      .then((data: { membershipPlans?: MembershipPlanConfig[]; paymentQrUrl?: string | null; paymentWechatId?: string; announcementEnabled?: boolean; announcementHtml?: string } | null) => {
        if (Array.isArray(data?.membershipPlans) && data.membershipPlans.length > 0) {
          setMembershipPlans(data.membershipPlans);
        }
        setPaymentQrUrl(data?.paymentQrUrl ?? null);
        setPaymentWechatId(data?.paymentWechatId ?? "");
        setAnnouncementEnabled(Boolean(data?.announcementEnabled && data.announcementHtml));
        setAnnouncementHtml(data?.announcementHtml ?? "");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const openMembershipPlans = () => {
      setMembershipNotice(null);
      setMembershipForceShowPlans(true);
      setMembershipOpen(true);
    };
    window.addEventListener("pi-web:open-membership-plans", openMembershipPlans);
    return () => window.removeEventListener("pi-web:open-membership-plans", openMembershipPlans);
  }, []);

  const [activeCwd, setActiveCwd] = useState<string | null>(null);

  useEffect(() => {
    if (window.location.search || window.location.hash) {
      router.replace("/chat", { scroll: false });
    }
  }, [router]);

  const handleCwdChange = useCallback((cwd: string | null) => {
    setActiveCwd(cwd);
    if (!cwd) return;
    // Close any session that belongs to a different cwd — it no longer
    // matches the selected project directory.
    setSelectedSession((prev) => {
      if (prev && prev.cwd !== cwd) return null;
      return prev;
    });
    setNewSessionCwd((prev) => {
      if (prev && prev !== cwd) return null;
      return prev;
    });
    setSessionKey((k) => k + 1);
    setBranchTree([]);
    setBranchActiveLeafId(null);
    setSystemPrompt(null);
    setActiveTopPanel(null);
    router.replace("/chat", { scroll: false });
  }, [router]);

  const handleSelectSession = useCallback((session: SessionInfo) => {
    setNewSessionCwd(null);
    setSelectedSession(session);
    setSessionKey((k) => k + 1);
    setSystemPrompt(null);
    router.replace("/chat", { scroll: false });
  }, [router]);

  const handleNewSession = useCallback((_sessionId: string, cwd: string) => {
    setSelectedSession(null);
    setNewSessionCwd(cwd);
    setSessionKey((k) => k + 1);
    setBranchTree([]);
    setBranchActiveLeafId(null);
    setSystemPrompt(null);
    setActiveTopPanel(null);
    router.replace("/chat", { scroll: false });
  }, [router]);

  // Called by ChatWindow when a new session gets its real id from pi
  const handleSessionCreated = useCallback((session: SessionInfo) => {
    setNewSessionCwd(null);
    setSelectedSession(session);
    setRefreshKey((k) => k + 1);
    router.replace("/chat", { scroll: false });
  }, [router]);

  const handleAgentEnd = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setExplorerRefreshKey((k) => k + 1);
    onUserRefresh?.();
  }, [onUserRefresh]);

  const handleSessionForked = useCallback((newSessionId: string) => {
    setRefreshKey((k) => k + 1);
    setSessionKey((k) => k + 1);
    setNewSessionCwd(null);
    setSelectedSession((prev) => ({
      ...(prev ?? { path: "", cwd: "", created: "", modified: "", messageCount: 0, firstMessage: "" }),
      id: newSessionId,
    }));
    router.replace("/chat", { scroll: false });
  }, [router]);

  const handleSessionDeleted = useCallback((sessionId: string) => {
    setRefreshKey((k) => k + 1);
    if (selectedSession?.id === sessionId) {
      const cwd = selectedSession.cwd;
      setSelectedSession(null);
      setNewSessionCwd(cwd ?? null);
      setSessionKey((k) => k + 1);
      setBranchTree([]);
      setBranchActiveLeafId(null);
      setSystemPrompt(null);
      setActiveTopPanel(null);
      router.replace("/chat", { scroll: false });
    }
  }, [selectedSession, router]);

  const handleOpenFile = useCallback((filePath: string, fileName: string) => {
    setEditingProfileFile({ filePath, fileName });
    setRightPanelOpen(false);
  }, []);

  const handleCreateProfile = useCallback(() => {
    const cwd = selectedSession?.cwd ?? newSessionCwd ?? activeCwd;
    if (selectedSession === null && cwd) {
      setNewSessionCwd(cwd);
      router.replace("/chat", { scroll: false });
    }
    const prompt = [
      "请引导我初始化长期关系档案。",
      "",
      "建档时请优先创建我的用户个人档案，再根据情况补充对方和关系信息。请先不要直接下结论，先用问题一步步收集信息；信息足够后，再帮我生成档案文件。",
      "",
      "第一阶段先收集我的个人档案：",
      "1. 我的基本情况",
      "2. 我的恋爱/长期关系目标",
      "3. 我的性格特点和沟通风格",
      "4. 我的情绪模式、需求和底线",
      "5. 我的择偶偏好、雷区和不能接受的事",
      "6. 我的过往关系经验和常见问题",
      "7. 当前最想解决的关系困扰",
      "",
      "第二阶段再询问是否需要补充长期关系档案：",
      "1. 对方基本情况",
      "2. 当前关系阶段",
      "3. 重要聊天/相处背景",
      "4. 对方性格、偏好和雷区",
      "5. 当前主要问题和后续沟通策略",
    ].join("\n");
    setProfilePromptRequest({
      id: typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      prompt,
    });
  }, [activeCwd, newSessionCwd, router, selectedSession]);

  const handleExportSession = useCallback(() => {
    if (!selectedSession) return;
    window.location.href = `/api/sessions/${encodeURIComponent(selectedSession.id)}/export`;
  }, [selectedSession]);

  // Show chat area if a session is selected, or if we have a cwd to start a new session in
  const effectiveNewSessionCwd = newSessionCwd ?? (selectedSession === null && activeCwd ? activeCwd : null);
  const showChat = selectedSession !== null || effectiveNewSessionCwd !== null;
  const showPlaceholder = !showChat;

  const currentMembershipPlan = membershipPlans.find((plan) => plan.id === (user?.membershipPlan ?? "free"));
  const currentPlanLabel = currentMembershipPlan?.name ?? PLAN_LABELS[user?.membershipPlan ?? "free"] ?? "会员";
  const quotaLabel = user?.remainingChats !== undefined ? `${user.remainingChats}/${user.monthlyQuota ?? "-"}` : null;
  const hasFreeQuotaRemaining = (user?.remainingChats ?? 0) > 0;

  const sidebarContent = (
    <>
      <SessionSidebar
        selectedSessionId={selectedSession?.id ?? null}
        onSelectSession={handleSelectSession}
        onNewSession={handleNewSession}
        refreshKey={refreshKey}
        onSessionDeleted={handleSessionDeleted}
        selectedCwd={selectedSession?.cwd ?? newSessionCwd ?? null}
        onCwdChange={handleCwdChange}
        onOpenFile={handleOpenFile}
        explorerRefreshKey={explorerRefreshKey}
        onAtMention={handleAtMention}
        onCreateProfile={handleCreateProfile}
      />
      {false && <div style={{ padding: "8px", flexShrink: 0, display: "flex", justifyContent: "space-between", gap: 4 }}>
        {([
          {
            label: "Models",
            onClick: () => setModelsConfigOpen(true),
            disabled: false,
            icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" />
                <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
                <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
                <line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" />
                <line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" />
              </svg>
            ),
          },
          {
            label: "Skills",
            onClick: () => setSkillsConfigOpen(true),
            disabled: !activeCwd && !selectedSession?.cwd && !newSessionCwd,
            icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            ),
          },
        ] as { label: string; onClick: () => void; disabled: boolean; icon: React.ReactNode }[]).map(({ label, onClick, disabled, icon }) => (
          <button
            key={label}
            onClick={onClick}
            disabled={disabled}
            title={label}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              height: 32, padding: 0, background: "none", border: "none",
              borderRadius: 9, color: "var(--text-muted)", cursor: disabled ? "default" : "pointer",
              fontSize: 12, opacity: disabled ? 0.35 : 1,
              transition: "background 0.12s, color 0.12s",
            }}
            onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = "var(--bg-hover)"; e.currentTarget.style.color = "var(--text)"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--text-muted)"; }}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>}
    </>
  );

  return (
    <>
    <div className="love-client-shell" style={{ display: "flex", height: "100dvh", overflow: "hidden", background: "var(--bg)" }}>
      {/* Mobile overlay backdrop */}
      <div
        className={`sidebar-overlay-backdrop${mobileSidebarReady ? "" : " sidebar-mobile-pending"}`}
        onClick={() => setSidebarOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 199,
          background: "rgba(0,0,0,0.4)",
          opacity: sidebarOpen ? 1 : 0,
          pointerEvents: sidebarOpen ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />

      {/* Left sidebar */}
      <div
        className={`sidebar-container love-client-sidebar${sidebarOpen ? " sidebar-open" : " sidebar-closed"}`}
        style={{
          background: "var(--bg-panel)",
          borderRight: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          zIndex: 200,
        }}
      >
        {sidebarContent}
      </div>

      {/* Center: chat */}
      <div className="love-client-main" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        {/* Top bar with sidebar toggle */}
        <div className="love-client-topbar" ref={topBarRef} style={{ display: "flex", alignItems: "center", flexShrink: 0, borderBottom: "1px solid var(--border)", height: 36, background: "var(--bg-panel)" }}>
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            title={sidebarOpen ? "隐藏侧边栏" : "显示侧边栏"}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, padding: 0,
              background: "none", border: "none", borderRight: "1px solid var(--border)",
              color: "var(--text-muted)", cursor: "pointer", flexShrink: 0, transition: "color 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
          >
            {sidebarOpen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              toggleTheme({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
            }}
            title={isDark ? "切换到浅色模式" : "切换到深色模式"}
            aria-label={isDark ? "切换到浅色模式" : "切换到深色模式"}
            aria-pressed={isDark}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, padding: 0,
              background: "none", border: "none", borderRight: "1px solid var(--border)",
              color: "var(--text-muted)", cursor: "pointer", flexShrink: 0, transition: "color 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
          >
            {isDark ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          {false && showChat && (
            <div style={{ display: "flex", alignItems: "stretch", height: "100%" }}>
              <button
                onClick={handleExportSession}
                disabled={!selectedSession}
                title={selectedSession ? "Export HTML" : "Export is available after the session is saved"}
                aria-label="Export HTML"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  height: "100%",
                  padding: "0 12px",
                  background: "none",
                  border: "none",
                  borderTop: "2px solid transparent",
                  borderRight: "1px solid var(--border)",
                  color: selectedSession ? "var(--text-muted)" : "var(--text-dim)",
                  cursor: selectedSession ? "pointer" : "not-allowed",
                  opacity: selectedSession ? 1 : 0.45,
                  flexShrink: 0,
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  transition: "color 0.1s, background 0.1s, opacity 0.1s",
                }}
                onMouseEnter={(e) => {
                  if (!selectedSession) return;
                  e.currentTarget.style.color = "var(--text)";
                  e.currentTarget.style.background = "var(--bg-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = selectedSession ? "var(--text-muted)" : "var(--text-dim)";
                  e.currentTarget.style.background = "none";
                }}
              >
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  background: "transparent",
                  color: selectedSession ? "var(--text-muted)" : "var(--text-dim)",
                  flexShrink: 0,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </span>
                {!isMobile && <span>Export</span>}
              </button>
              <BranchNavigator
                tree={branchTree}
                activeLeafId={branchActiveLeafId}
                onLeafChange={handleBranchLeafChange}
                inline
                compact={isMobile}
                containerRef={topBarRef}
                open={activeTopPanel === "branches"}
                onToggle={() => toggleTopPanel("branches")}
                hasSession
              />
              <button
                ref={systemBtnRef}
                onClick={() => toggleTopPanel("system")}
                title="System prompt"
                aria-label="System prompt"
                aria-pressed={activeTopPanel === "system"}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  height: "100%", padding: "0 12px",
                  background: activeTopPanel === "system" ? "var(--bg-selected)" : "none",
                  border: "none",
                  borderTop: activeTopPanel === "system" ? "2px solid var(--accent)" : "2px solid transparent",
                  borderRight: "1px solid var(--border)",
                  cursor: "pointer",
                  color: activeTopPanel === "system" ? "var(--text)" : "var(--text-muted)",
                  fontSize: 11, whiteSpace: "nowrap", transition: "color 0.1s, background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = activeTopPanel === "system" ? "var(--text)" : "var(--text-muted)"; }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: systemPrompt ? "var(--accent)" : "var(--text-dim)", flexShrink: 0 }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="13" y2="17" />
                </svg>
                <span>系统</span>
              </button>
            </div>
          )}
          <div className="love-client-account-bar" style={{ marginLeft: "auto", display: "flex", alignItems: "center", height: "100%", borderLeft: "1px solid var(--border)" }}>
            {user && (
              <button
                className="love-client-account-button"
                type="button"
                onClick={() => {
                  setMembershipNotice(null);
                  setMembershipForceShowPlans(false);
                  setMembershipOpen(true);
                }}
                title={quotaLabel ? `${user.username} | ${currentPlanLabel} | 剩余 ${user.remainingChats} / ${user.monthlyQuota ?? "-"} 次 | ${user.id}` : `${user.username} | ${user.id}`}
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "0 12px",
                  border: "none",
                  background: "none",
                  fontSize: 11,
                  color: "var(--text-muted)",
                  maxWidth: "min(34vw, 320px)",
                  minWidth: 0,
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
              >
                <span className="love-client-account-text" style={{ minWidth: 0, overflow: "hidden", textAlign: "left", lineHeight: 1.15 }}>
                  <span
                    className="love-client-account-name"
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "var(--text)",
                      fontWeight: 600,
                    }}
                  >
                    {user.username}
                  </span>
                  <span
                    className="love-client-account-plan"
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginTop: 2,
                      fontSize: 10,
                      color: "var(--text-dim)",
                    }}
                  >
                    {currentPlanLabel}
                  </span>
                </span>
                {quotaLabel && (
                  <span
                    className="love-client-account-quota"
                    style={{
                      flexShrink: 0,
                      padding: "2px 7px",
                      borderRadius: 999,
                      border: "1px solid var(--border)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 11,
                      fontWeight: 650,
                      lineHeight: 1.2,
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {quotaLabel}
                  </span>
                )}
              </button>
            )}
            {user?.role === "admin" && (
              <a
                className="love-client-admin-link"
                href="/admin"
                title="管理后台"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  height: 36, padding: "0 10px",
                  background: "none", borderLeft: "1px solid var(--border)",
                  color: "var(--text-muted)", textDecoration: "none", fontSize: 11, flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
              >
                管理
              </a>
            )}
            <button
              className="love-client-logout-button"
              onClick={handleLogout}
              title="退出登录"
              aria-label="退出登录"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, padding: 0,
                background: "none", border: "none", borderLeft: "1px solid var(--border)",
                color: "var(--text-muted)", cursor: "pointer", flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
          {/* Top panel dropdown — shared, only one active at a time */}
          {activeTopPanel && topPanelPos && (
            <div style={{
              position: "fixed",
              top: topPanelPos.top,
              left: topPanelPos.left,
              width: topPanelPos.width,
              maxHeight: `calc(100dvh - ${topPanelPos.top}px)`,
              overflowY: "auto",
              zIndex: 500,
            }}>
              {activeTopPanel === "system" && (
                <div style={{
                  background: "var(--bg-panel)",
                  borderBottom: "1px solid var(--border)",
                }}>
                  {systemPrompt ? (
                    <div style={{
                      maxHeight: "min(600px, 75vh)",
                      overflowY: "auto",
                      padding: "12px 16px",
                      color: "var(--text-muted)",
                      fontSize: 12,
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      fontFamily: "var(--font-mono)",
                    }}>
                      {systemPrompt}
                    </div>
                  ) : systemPrompt === "" ? (
                    <div style={{ padding: "10px 16px", fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                      系统提示词为空（工具已禁用）
                    </div>
                  ) : (
                    <div style={{ padding: "10px 16px", fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                      发送消息后加载系统提示词
                    </div>
                  )}
                </div>
              )}
              {activeTopPanel === "session" && (
                <div className="session-info-popover" style={{
                  background: "var(--bg-panel)",
                  borderBottom: "1px solid var(--border)",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.10)",
                  padding: "12px 16px",
                }}>
                  {sessionStats ? (() => {
                    const sessionRows = [
                      ...(sessionStats.sessionName ? [{ label: "Name", value: sessionStats.sessionName, copyField: null }] : []),
                      { label: "File", value: sessionStats.sessionFile ?? "In-memory", copyField: "file" as const },
                      { label: "ID", value: sessionStats.sessionId, copyField: "id" as const },
                    ];
                    const messageRows = [
                      ["User", sessionStats.userMessages.toLocaleString()],
                      ["Assistant", sessionStats.assistantMessages.toLocaleString()],
                      ["Tool Calls", sessionStats.toolCalls.toLocaleString()],
                      ["Tool Results", sessionStats.toolResults.toLocaleString()],
                      ["Total", sessionStats.totalMessages.toLocaleString()],
                    ];
                    const tokenRows = [
                      ["Input", sessionStats.tokens.input.toLocaleString()],
                      ["Output", sessionStats.tokens.output.toLocaleString()],
                      ...(sessionStats.tokens.cacheRead > 0 ? [["Cache Read", sessionStats.tokens.cacheRead.toLocaleString()]] : []),
                      ...(sessionStats.tokens.cacheWrite > 0 ? [["Cache Write", sessionStats.tokens.cacheWrite.toLocaleString()]] : []),
                      ["Total", sessionStats.tokens.total.toLocaleString()],
                    ];
                    const ctx = contextUsage ?? sessionStats.contextUsage;
                    const formatCompact = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
                    const extraTokenRows = [
                      ...(sessionStats.cost > 0 ? [["Cost", `$${sessionStats.cost.toFixed(4)}`]] : []),
                      ...(ctx?.contextWindow ? [["Context", `${ctx.percent !== null ? `${ctx.percent.toFixed(1)}%` : "?"} / ${formatCompact(ctx.contextWindow)}`]] : []),
                    ];
                    const section = (
                      title: string,
                      sectionRows: string[][],
                      valueAlign: "left" | "right" = "left",
                      compact = false,
                    ) => (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>{title}</div>
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: compact ? "max-content max-content" : "auto minmax(0, 1fr)",
                            columnGap: compact ? 14 : 12,
                            rowGap: 4,
                            justifyContent: compact ? "start" : undefined,
                          }}>
                            {sectionRows.map(([label, value]) => (
                              <div key={`${title}:${label}`} style={{ display: "contents" }}>
                                <div style={{ color: "var(--text-dim)", whiteSpace: "nowrap" }}>{label}</div>
                                <div style={{
                                  color: "var(--text-muted)",
                                  minWidth: 0,
                                  overflowWrap: compact ? "normal" : "anywhere",
                                  textAlign: valueAlign,
                                  whiteSpace: valueAlign === "right" ? "nowrap" : "normal",
                                }}>{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    const copyButton = (field: SessionCopyField, value: string) => {
                      const copied = copiedSessionField === field;
                      return (
                        <button
                          type="button"
                          title={copied ? "Copied" : `Copy ${field === "file" ? "file path" : "session ID"}`}
                          onClick={() => handleCopySessionField(field, value)}
                          style={{
                            alignSelf: "start",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 22,
                            height: 22,
                            marginTop: -2,
                            color: copied ? "var(--accent)" : "var(--text-dim)",
                            background: "transparent",
                            border: "1px solid var(--border)",
                            borderRadius: 4,
                            cursor: "pointer",
                            flex: "0 0 auto",
                            transition: "color 0.12s, border-color 0.12s, background 0.12s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "var(--accent)";
                            e.currentTarget.style.borderColor = "var(--accent)";
                            e.currentTarget.style.background = "var(--bg-hover)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = copied ? "var(--accent)" : "var(--text-dim)";
                            e.currentTarget.style.borderColor = "var(--border)";
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {copied ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          )}
                        </button>
                      );
                    };
                    const sessionInfoSection = (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>Session Info</div>
                        <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", columnGap: 12, rowGap: 8, alignItems: "start" }}>
                          {sessionRows.map((row) => (
                            <div key={`session-info:${row.label}`} style={{ display: "contents" }}>
                              <div style={{ color: "var(--text-dim)", whiteSpace: "nowrap" }}>{row.label}</div>
                              <div style={{
                                color: "var(--text-muted)",
                                minWidth: 0,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                              }}>{row.value}</div>
                              <div>{row.copyField ? copyButton(row.copyField, row.value) : null}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );

                    return (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "minmax(360px, 1.7fr) minmax(140px, 0.55fr) minmax(190px, 0.75fr)",
                        gap: isMobile ? 16 : 24,
                        fontSize: 12,
                        lineHeight: 1.5,
                        fontFamily: "var(--font-mono)",
                      }}>
                        {sessionInfoSection}
                        {section("Messages", messageRows)}
                        {section("Tokens", [...tokenRows, ...extraTokenRows], "right", true)}
                      </div>
                    );
                  })() : (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                      Send a message or run /session to load session info
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>

        {announcementEnabled && announcementHtml && (
          <button
            type="button"
            onClick={() => setAnnouncementOpen(true)}
            className="love-announcement-bar"
            style={{
              border: "none",
              borderBottom: "1px solid var(--border)",
              background: "color-mix(in srgb, var(--accent) 8%, var(--bg-panel))",
              color: "var(--text)",
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              textAlign: "left",
              cursor: "pointer",
              flexShrink: 0,
              minHeight: 42,
            }}
          >
            <span style={{
              flexShrink: 0,
              border: "1px solid color-mix(in srgb, var(--accent) 36%, transparent)",
              borderRadius: 999,
              color: "var(--accent)",
              background: "var(--bg-panel)",
              padding: "2px 8px",
              fontSize: 11,
              fontWeight: 750,
            }}>
              公告
            </span>
            <span
              className="announcement-rich-content love-announcement-preview"
              dangerouslySetInnerHTML={{ __html: announcementHtml }}
            />
            <span style={{ marginLeft: "auto", flexShrink: 0, color: "var(--text-muted)", fontSize: 12 }}>查看详情</span>
          </button>
        )}

        {/* Chat content */}
        <div className="love-client-chat-stage" style={{ flex: 1, overflow: "hidden", position: "relative" }}>
          {showChat ? (
            <ChatWindow
              key={sessionKey}
              session={selectedSession}
              newSessionCwd={effectiveNewSessionCwd}
              onAgentEnd={handleAgentEnd}
              onSessionCreated={handleSessionCreated}
              onSessionForked={handleSessionForked}
              onQuotaChanged={onUserRefresh}
              modelsRefreshKey={modelsRefreshKey}
              chatInputRef={chatInputRef}
              onBranchDataChange={handleBranchDataChange}
              onSystemPromptChange={handleSystemPromptChange}
              onSessionStatsChange={handleSessionStatsChange}
              onSessionStatsPanelOpen={openSessionStatsPanel}
              onContextUsageChange={handleContextUsageChange}
              onOpenMembership={() => {
                setMembershipNotice(null);
                setMembershipForceShowPlans(true);
                setMembershipOpen(true);
              }}
              remainingChats={user?.remainingChats}
              autoStartPrompt={profilePromptRequest}
              onAutoStartPromptConsumed={(id) => {
                setProfilePromptRequest((current) => current?.id === id ? null : current);
              }}
            />
          ) : showPlaceholder ? (
            activeCwd ? (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 15 }}>
                从侧边栏选择一个会话
              </div>
            ) : (
              <div style={{ position: "absolute", top: 12, left: 12, display: "flex", alignItems: "flex-start", gap: 8, userSelect: "none", pointerEvents: "none" }}>
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, flexShrink: 0 }}>
                  <line x1="20" y1="12" x2="4" y2="12" /><polyline points="10 6 4 12 10 18" />
                </svg>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>开始使用</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.8 }}>
                    <span style={{ color: "var(--text-dim)", marginRight: 6 }}>1.</span>点击左侧新建会话<br />
                    <span style={{ color: "var(--text-dim)", marginRight: 6 }}>2.</span>输入问题后直接开始对话
                  </div>
                </div>
              </div>
            )
          ) : null}
        </div>
      </div>

      {/* File preview panel hidden by request. */}
    </div>
    {/* File panel toggle — always visible at top-right */}
    {false && <button
      onClick={() => setRightPanelOpen((v) => !v)}
      title={rightPanelOpen ? "隐藏文件面板" : "显示文件面板"}
      style={{
        position: "fixed", top: 0, right: 0, zIndex: 300,
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 36, height: 36, padding: 0,
        background: "var(--bg-panel)", border: "none", borderLeft: "1px solid var(--border)", borderBottom: "1px solid var(--border)",
        color: rightPanelOpen ? "var(--text)" : "var(--text-muted)",
        cursor: "pointer", transition: "color 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = rightPanelOpen ? "var(--text)" : "var(--text-muted)"; }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="15" y1="3" x2="15" y2="21" />
      </svg>
    </button>}
    {false && modelsConfigOpen && <ModelsConfig onClose={() => { setModelsConfigOpen(false); setModelsRefreshKey((k) => k + 1); }} />}
    {false && skillsConfigOpen && (activeCwd ?? selectedSession?.cwd ?? newSessionCwd) && (
      <SkillsConfig cwd={(activeCwd ?? selectedSession?.cwd ?? newSessionCwd)!} onClose={() => setSkillsConfigOpen(false)} />
    )}
    {editingProfileFile && (
      <ProfileFileEditor
        filePath={editingProfileFile.filePath}
        cwd={activeCwd ?? selectedSession?.cwd ?? newSessionCwd ?? undefined}
        onClose={() => setEditingProfileFile(null)}
        onSaved={() => setExplorerRefreshKey((key) => key + 1)}
      />
    )}
    {membershipOpen && user && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="membership-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 900,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(15,23,42,0.32)",
          padding: 20,
        }}
        onClick={() => {
          setMembershipOpen(false);
          setMembershipForceShowPlans(false);
          setMembershipNotice(null);
        }}
      >
        <div
          style={{
            width: "min(760px, 100%)",
            maxHeight: "min(720px, 92vh)",
            overflowY: "auto",
            background: "var(--bg-panel)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
            color: "var(--text)",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <h2 id="membership-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{hasFreeQuotaRemaining && !membershipForceShowPlans ? "账号额度" : "会员套餐"}</h2>
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-muted)" }}>
                当前账号：{user.username} · {currentPlanLabel} · 剩余 {user.remainingChats ?? "-"} / {user.monthlyQuota ?? "-"} 次
              </div>
            </div>
            <button
              onClick={() => {
                setMembershipOpen(false);
                setMembershipForceShowPlans(false);
                setMembershipNotice(null);
              }}
              title="关闭"
              aria-label="关闭"
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: 7,
                background: "none",
                color: "var(--text-muted)",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-hover)"; e.currentTarget.style.color = "var(--text)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--text-muted)"; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div style={{ padding: 20 }}>
            {membershipNotice && (
              <div style={{
                marginBottom: 14,
                padding: "10px 12px",
                border: "1px solid color-mix(in srgb, var(--accent) 24%, transparent)",
                borderRadius: 8,
                background: "color-mix(in srgb, var(--accent) 8%, transparent)",
                color: "var(--text)",
                fontSize: 13,
                lineHeight: 1.6,
              }}>
                {membershipNotice}
              </div>
            )}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 10,
              marginBottom: 14,
            }}>
              <div style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg)" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5 }}>会员状态</div>
                <div style={{ fontSize: 14, fontWeight: 650 }}>{user.membershipStatus === "expired" ? "已过期" : "生效中"}</div>
              </div>
              <div style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg)" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5 }}>会员到期</div>
                <div style={{ fontSize: 14, fontWeight: 650 }}>{formatDate(user.membershipExpiresAt)}</div>
              </div>
              <div style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg)" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5 }}>次数周期</div>
                <div style={{ fontSize: 14, fontWeight: 650 }}>{formatDate(user.quotaPeriodEnd)}</div>
              </div>
            </div>

            {hasFreeQuotaRemaining && !membershipForceShowPlans ? (
              <div style={{
                border: "1px solid color-mix(in srgb, var(--accent) 22%, transparent)",
                borderRadius: 8,
                background: "color-mix(in srgb, var(--accent) 7%, transparent)",
                padding: 14,
                color: "var(--text)",
                fontSize: 13,
                lineHeight: 1.7,
              }}>
                当前还有 {user.remainingChats ?? 0} 次免费对话，套餐内容会在免费次数用完后显示。
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 12,
              }}>
                {membershipPlans.map((plan) => {
                  const current = (user.membershipPlan ?? "free") === plan.id;
                  const discountActive = hasPlanDiscount(plan);
                  const planBadges = getPlanBadges(plan, discountActive);
                  const highlighted = plan.id === "lifetime" || discountActive;
                  const currentPriceText = formatPlanPrice(plan.price, plan.id) ?? plan.priceLabel;
                  const originalPriceText = formatPlanPrice(plan.originalPrice, plan.id);
                  const selectPlan = () => {
                    if (current) return;
                    setMembershipNotice(`已选择「${plan.name}」。请联系管理员开通，管理员可在管理后台为你的账号修改会员套餐和剩余次数。`);
                  };
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={selectPlan}
                      disabled={current}
                      style={{
                        textAlign: "left",
                        border: `1px solid ${current || highlighted ? "color-mix(in srgb, var(--accent) 48%, var(--border))" : "var(--border)"}`,
                        borderRadius: 8,
                        background: current || highlighted
                          ? "linear-gradient(180deg, color-mix(in srgb, var(--accent) 9%, var(--bg-panel)), var(--bg))"
                          : "var(--bg)",
                        padding: 14,
                        minHeight: 180,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        color: "var(--text)",
                        cursor: current ? "default" : "pointer",
                        font: "inherit",
                      }}
                      onMouseEnter={(event) => {
                        if (current) return;
                        event.currentTarget.style.borderColor = "color-mix(in srgb, var(--accent) 55%, var(--border))";
                        event.currentTarget.style.background = "color-mix(in srgb, var(--accent) 4%, var(--bg))";
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.borderColor = current || highlighted ? "color-mix(in srgb, var(--accent) 48%, var(--border))" : "var(--border)";
                        event.currentTarget.style.background = current || highlighted
                          ? "linear-gradient(180deg, color-mix(in srgb, var(--accent) 9%, var(--bg-panel)), var(--bg))"
                          : "var(--bg)";
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>{plan.name}</div>
                            {planBadges.map((badge) => (
                              <span key={badge} style={{
                                fontSize: 10,
                                fontWeight: 750,
                                color: "#fff",
                                background: badge === "热销" ? "linear-gradient(135deg, #f59e0b, #e11d48)" : "var(--accent)",
                                borderRadius: 999,
                                padding: "2px 7px",
                                lineHeight: 1.4,
                                boxShadow: badge === "热销" ? "0 8px 18px rgba(225,29,72,0.18)" : "none",
                              }}>
                                {badge}
                              </span>
                            ))}
                          </div>
                          <div style={{ marginTop: 6, display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 19, fontWeight: 800, color: "var(--text)" }}>
                              {discountActive ? currentPriceText : plan.priceLabel}
                            </span>
                            {discountActive && originalPriceText && (
                              <span style={{ fontSize: 12, color: "var(--text-dim)", textDecoration: "line-through" }}>
                                {originalPriceText}
                              </span>
                            )}
                          </div>
                        </div>
                        {current && (
                          <span style={{
                            fontSize: 11,
                            color: "var(--accent)",
                            border: "1px solid color-mix(in srgb, var(--accent) 45%, transparent)",
                            borderRadius: 999,
                            padding: "2px 7px",
                            whiteSpace: "nowrap",
                          }}>
                            当前
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 7, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
                        <div>每月 {plan.monthlyQuota} 次对话</div>
                        <div>{plan.desc}</div>
                      </div>
                      <div style={{ marginTop: "auto", fontSize: 12, color: current ? "var(--accent)" : "var(--text-muted)" }}>
                        {current ? "你正在使用此套餐" : "点击选择此套餐"}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {(!hasFreeQuotaRemaining || membershipForceShowPlans) && (paymentQrUrl || paymentWechatId) && (
              <div
                style={{
                  marginTop: 16,
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  background: "var(--bg)",
                  padding: 14,
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {paymentQrUrl && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={paymentQrUrl}
                    alt="会员开通二维码"
                    style={{
                      width: 132,
                      height: 132,
                      objectFit: "contain",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      background: "#fff",
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ minWidth: 220, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>扫码或加微信开通会员</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>
                    选择套餐后，请通过二维码或微信联系管理员开通。管理员确认后会为你的账号修改会员套餐和剩余次数。
                  </div>
                  {paymentWechatId && (
                    <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid var(--border)", borderRadius: 7, background: "var(--bg-panel)", padding: "7px 10px", fontSize: 13 }}>
                      <span style={{ color: "var(--text-muted)" }}>微信号</span>
                      <span style={{ fontWeight: 700, color: "var(--text)" }}>{paymentWechatId}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    {announcementOpen && announcementEnabled && announcementHtml && (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-title"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 920,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(15,23,42,0.32)",
          padding: 20,
        }}
        onClick={() => setAnnouncementOpen(false)}
      >
        <div
          style={{
            width: "min(680px, 100%)",
            maxHeight: "min(680px, 90vh)",
            overflowY: "auto",
            background: "var(--bg-panel)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
            color: "var(--text)",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <h2 id="announcement-title" style={{ margin: 0, fontSize: 18, fontWeight: 750 }}>公告</h2>
            <button
              onClick={() => setAnnouncementOpen(false)}
              title="关闭"
              aria-label="关闭"
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: 7,
                background: "none",
                color: "var(--text-muted)",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-hover)"; e.currentTarget.style.color = "var(--text)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--text-muted)"; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div style={{ padding: 18 }}>
            <div className="announcement-rich-content" dangerouslySetInnerHTML={{ __html: announcementHtml }} />
          </div>
        </div>
      </div>
    )}
    </>
  );
}
